import { useState } from "react";
import { CheckCircle2, LoaderCircle, Play, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "../../lib/toast";
import type { ContractMasterRow, PrMasterRow } from "./prCatalog";

export interface AgentEvaluation {
  classification: string;
  matchedContract: string;
  recommendationStrength: string;
  details: string;
  cId?: string;
  mId?: string;
}

export interface PrRecord {
  eligibilityId: string;
  prId: string;
  supplier: string;
  supplierId?: string;
  category: string;
  amount: number | null;
  currency: string;
  description: string;
  isRecurring: boolean;
  isEligible: boolean;
  reason: string;
  catalog?: PrMasterRow;
  contract?: ContractMasterRow;
  savedId?: number;
  backendDataUrl?: string;
  agentEvaluation?: AgentEvaluation;
}

// "01-Jan-2026" -> "Jan 2026", for the contract date range shown next to Matched Contract.
const formatContractDate = (value?: string) => {
  if (!value) return "";
  const parts = value.split("-");
  return parts.length === 3 ? `${parts[1]} ${parts[2]}` : value;
};

// The agent only returns a qualitative strength ("High Confidence"); map it to an
// indicative percentage so the UI can show the same number + bar the design calls for.
const strengthToPercent = (strength: string) => {
  const numeric = strength.match(/(\d{1,3})\s*%/);
  if (numeric) return Math.min(100, Number(numeric[1]));
  const lower = strength.toLowerCase();
  if (lower.includes("high")) return 92;
  if (lower.includes("medium") || lower.includes("moderate")) return 68;
  if (lower.includes("low")) return 42;
  return 75;
};

const CLASSIFICATION_LABELS = ["IAC-Eligible", "PR Required", "Human Validation", "Use Existing PR"];

// The agent endpoint currently returns a generic workflow status ("success"/"failure") rather than
// one of the eligibility labels this app is modeled on, so fall back to the eligibility already
// computed locally against the contract master whenever the AI text isn't one of the known labels.
const resolveClassificationLabel = (raw: string, isEligible: boolean) => {
  const normalized = raw.trim().toLowerCase();
  const known = CLASSIFICATION_LABELS.find((label) => normalized.includes(label.toLowerCase()));
  if (known) return known;
  return isEligible ? "IAC-Eligible" : "PR Required";
};

export function PrDetailsPanel({
  record,
  onRunAgent,
}: {
  record: PrRecord;
  onRunAgent: () => Promise<void>;
}) {
  const [isRunning, setIsRunning] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);

  const runAgent = async () => {
    setIsRunning(true);
    setAgentError(null);
    try {
      await onRunAgent();
    } catch (error) {
      setAgentError(error instanceof Error ? error.message : "Unable to complete agent evaluation.");
    } finally {
      setIsRunning(false);
    }
  };

  const notifyAuditLog = (description: string) => {
    const timestamp = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    toast.success("Added to Audit Log / History", { description: `${timestamp} — ${description}` });
  };

  const confirmSuppression = () => {
    notifyAuditLog(`PR/PO creation was suppressed for ${record.prId} — this spend is IAC-eligible.`);
  };

  const overrideRecommendation = () => {
    notifyAuditLog("Human override the recommendation — a PR/PO was created despite IAC eligibility.");
  };

  const evaluation = record.agentEvaluation;
  const confidencePercent = evaluation ? strengthToPercent(evaluation.recommendationStrength) : 0;
  const matchedContractLabel = record.contract
    ? `${record.contract.contractId} — ${record.contract.supplierName}, ${record.contract.category} (${formatContractDate(record.contract.startDate)} – ${formatContractDate(record.contract.endDate)})`
    : evaluation?.matchedContract;
  const classificationLabel = evaluation ? resolveClassificationLabel(evaluation.classification, record.isEligible) : "";
  const isSuppressible = classificationLabel === "IAC-Eligible";
  const suppressionContractId = record.contract?.contractId || evaluation?.matchedContract || "the matched contract";
  const reasonText = record.contract
    ? `This spend matches active Contract ${record.contract.contractId} for the same supplier and category, with terms covering this transaction type and amount.`
    : evaluation?.details;

  return (
    <section className="iac-engine__created-page">
      <div className="iac-engine__created-card">
        <div className="iac-engine__created-header">
          <div className="iac-engine__created-title"><CheckCircle2 size={22} /> <strong>{record.prId} Created Successfully</strong></div>
          <p>To view all the PR click to <u>PR listing</u> page, or <u>Check new</u></p>
        </div>

        <div className="iac-engine__created-summary">
          <div><span>Supplier</span><strong>{record.supplier}</strong></div>
          <div><span>Supplier Code</span><strong>{record.supplierId || "—"}</strong></div>
          <div><span>Amount</span><strong>{record.amount == null ? "—" : `${record.currency === "USD" ? "$" : "₹"}${record.amount.toLocaleString("en-US")}`}</strong></div>
          <div><span>Recurring?</span><strong>{record.isRecurring ? "Yes" : "No"}</strong></div>
          <div className="iac-engine__created-description"><span>Description:</span> {record.description}</div>
          {!record.agentEvaluation && (
            <button type="button" className="iac-engine__btn iac-engine__btn--primary" onClick={() => void runAgent()} disabled={isRunning}>
              {isRunning ? <LoaderCircle size={15} className="iac-engine__spin" /> : <Play size={15} />}
              {isRunning ? "Running Agent Evaluation..." : "Run Agent Evaluation"}
            </button>
          )}
        </div>

        {agentError && !record.agentEvaluation && (
          <p className="iac-engine__agent-error">{agentError}</p>
        )}

        {evaluation && (
          <div className="iac-engine__evaluation">
            <div className="iac-engine__evaluation-top">
              <div className="iac-engine__evaluation-classification">
                <span>CLASSIFICATION</span>
                <strong>{classificationLabel}</strong>
              </div>
              <div className="iac-engine__evaluation-strength">
                <span>RECOMMENDATION STRENGTH</span>
                <strong className="iac-engine__evaluation-percent">{confidencePercent}%</strong>
                <small>{evaluation.recommendationStrength}</small>
              </div>
            </div>
            <div className="iac-engine__evaluation-bar">
              <div className="iac-engine__evaluation-bar-fill" style={{ width: `${confidencePercent}%` }} />
            </div>
            <div className="iac-engine__evaluation-contract"><span>Matched Contract:</span> <strong>{matchedContractLabel}</strong></div>
            <p className="iac-engine__evaluation-details">{reasonText}</p>
            <div className="iac-engine__evaluation-notice">
              <ShieldCheck size={18} />
              {isSuppressible ? (
                <span>
                  Heads up — <strong>{record.prId}</strong> for Supplier <strong>{record.supplier}</strong> appears to
                  qualify for direct invoice processing against Contract <strong>{suppressionContractId}</strong>.
                  Creating a PO may not be necessary. Please confirm or override below.
                </span>
              ) : (
                <span>This recommendation was generated from the IAC agent response for this supplier and description.</span>
              )}
            </div>
            <div className="iac-engine__evaluation-actions">
              <button type="button" className="iac-engine__btn iac-engine__btn--primary" onClick={confirmSuppression}><CheckCircle2 size={15} /> Confirm Suppression</button>
              <button type="button" className="iac-engine__btn iac-engine__btn--outline" onClick={overrideRecommendation}>Override — Create PR Anyway</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
